import React, { useContext, useEffect, useCallback, useState } from 'react';
import Button from '@material-ui/core/Button';
import Snackbar from '@material-ui/core/Snackbar';
import _ from 'lodash';
import logger from '../../logger';

import { filterCustomerCampaigns, updateSurveysWorkspace } from '../../utils/miscFunctions';
import TableDisplay from '../TableDisplay';
import FiltersDisplay from '../FiltersDisplay';
import EditCampaignModal from '../../components/Campaigns/EditCampaignModal';
import VaultApiRequestService from '../../VaultApiRequestService';
import { SurveyContext } from '../../SurveyContext';
import { isActiveTypes } from '../../utils/enums';

const CustomerCampaigns = (props) => {
    
    const { customers, fetchAndUpdateCustomers } = useContext(SurveyContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState(isActiveTypes.ALL);
    const [campaigns, setCampaigns] = useState([]);
    const [customer, setCustomer] = useState(undefined);
    const [create, setCreate] = useState(false);
    const [edit, setEdit] = useState(false);
    const [editingCampaign, setEditingCampaign] = useState(undefined);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState(false);

    const getCampaigns = useCallback(() => {
        const id = props.match.params.id;
        let customer = customers.find(customer => customer.id === parseInt(id, 10));
        setCustomer(customer);
        return customer?.campaigns || [];
    }, [customers, props.match.params.id]);

    useEffect(() => {
        const campaigns = getCampaigns();
        setCampaigns(campaigns);
      }, [customers, getCampaigns]);

    useEffect(() => {
    let campaigns = getCampaigns();
    if (searchTerm !== '') {
        campaigns = campaigns.filter(filterCustomerCampaigns(searchTerm));
    }
    campaigns = campaigns.filter(row => {
        if (statusFilter === isActiveTypes.ALL) {
            return true;
        }
        return row.is_active === statusFilter;
    });
    setCampaigns(campaigns);
    }, [getCampaigns, searchTerm, statusFilter]);

    const onCampaignClick = (data) => {
        props.history.push(`/campaigns/${customer.id}/surveys/${data.id}`);
    }

    const addNewCampaign = async (payload) => {
        try {
            const response  = await VaultApiRequestService.addCampaign(payload);
            if (!response?.data) {
                logger.exception(
                    'Error saving campaign to Vault',
                    _.get(response, 'message'),
                    { payload }
                );
                setSnackbarMessage('Error saving campaign to Vault');
                setOpenSnackbar(true);
            } else {
                await fetchAndUpdateCustomers();
            }
            setCreate(false);
        } catch (err) {
            logger.error(err, { action: 'Create campaign in Vault', payload });
            setSnackbarMessage('Error saving campaign to Vault');
            setOpenSnackbar(true);
            setCreate(false);
        }
    };

    const updateSurveys = async(isActive) => {
        const changeWorkspaceSuccess = await updateSurveysWorkspace({ isActive, surveys: editingCampaign.surveys });
        if (!changeWorkspaceSuccess) {
            logger.exception(
                'Error changing surveys workspace in Typeform',
                '',
                { isActive, surveys: editingCampaign.surveys }
            );
            setSnackbarMessage("Error changing surveys workspace in Typeform");
            setOpenSnackbar(true);
        }
    }

    const updateExistingCampaign = async (payload) => {
        try {
            const leanCampaign = {
                client: customer.id, 
                ..._.pick(editingCampaign, ['type', 'is_active', 'name'])
            };
            if (_.isEqual(payload, leanCampaign)) {
                    setEdit(false);
            } else {
                let leanPayload = _.omitBy(payload, _.isUndefined);
                if (editingCampaign.is_active === leanPayload.is_active) {
                    leanPayload = _.omit(leanPayload, ['is_active']);
                }
                const response  = await VaultApiRequestService.editCampaign(
                    leanPayload, editingCampaign.id
                );
                if (!response?.data) {
                    logger.exception(
                        'Error editing campaign in Vault',
                        _.get(response, 'message'),
                        { payload }
                    );
                    setSnackbarMessage('Error editing campaign in Vault');
                    setOpenSnackbar(true);
                } else {
                    // update typeform surveys workspace
                    if(!_.isUndefined(_.get(leanPayload,'is_active'))) {
                        await updateSurveys(_.get(leanPayload,'is_active'));
                    }
                    await fetchAndUpdateCustomers();
                }
                setEdit(false);
            }
        } catch (err) {
            logger.error(err, { action: 'Edit campaign', payload });
            setSnackbarMessage('Error editing campaign in Vault');
            setOpenSnackbar(true);
            setEdit(false);
        }
    };

    const onEditClick = (campaignId) => {
        const campaign = campaigns.find(campaign => campaign.id === campaignId);
        setEditingCampaign(campaign);
        setEdit(true);
    }

    return (
        <div>
            <h2 style={{ textAlign: 'center', marginRight: 10 }}>Campaigns</h2>
       
            <FiltersDisplay
                setSearchTerm={setSearchTerm}
                setStatusFilter={setStatusFilter}
                searchTerm={searchTerm}
            />
            <div style={{ display: 'flex', position: 'relative' }}>
                <Button 
                    variant="outlined" 
                    color="primary" 
                    onClick={() => props.history.push('/customers')}
                >
                    All Customers
                </Button>
                <Button 
                    style={{ position: 'absolute', right: 0 }}
                    variant="outlined" 
                    color="primary" 
                    onClick={() => setCreate(true)}
                >
                    New Campaign
                </Button>
            </div>
            {create &&
                <EditCampaignModal
                    saveClick={addNewCampaign}
                    isActive={true}
                    type={''}
                    name={''}
                    customer={customer}
                    open={create}
                    onClose={() => setCreate(false)}
                    action="new"
                />
            }   
            <TableDisplay 
                data={campaigns}
                tableHeaders={["Name", "Active", "Type", "Surveys"]}
                tableFields={["name", "is_active", "type", "surveys.length"]}
                onRowClick={onCampaignClick}
                onEditClick={onEditClick}
            />
            {edit &&    
                <EditCampaignModal
                    saveClick={updateExistingCampaign}
                    isActive={editingCampaign.is_active}
                    type={editingCampaign.type}
                    name={editingCampaign.name}
                    customer={customer}
                    open={edit}
                    onClose={() => {
                        // Clear state when modal closes.
                        setEditingCampaign(undefined);
                        setEdit(false);
                    }}
                />
            }
            <Snackbar
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={() => setOpenSnackbar(!openSnackbar)}
                message={snackbarMessage}
                style={{ fontSize: 14 }}
            />
        </div>
    )

}
export default CustomerCampaigns;

